import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { useUser } from "../providers/UserProvider";
import { useToggleTop } from "../components/common/ToggleTop";
import AlertDialog from "../components/layout/app/AlertDialog";
import { AutomationService } from "./service";
import { useAutomationFeature } from "./AutomationProvider";

// The Automation screens' data, as the app's app/automation/useAutomations.js.

export const errorMessage = (error) => error?.message || "Something went wrong. Please try again.";

// Owners (including members it was shared with as owner) change automations;
// the API checks it too.
export function useCanManageAutomations() {
  const { profileName } = useUser();
  const { homeId } = useAutomationFeature();
  return (profileName.find((house) => house.id === homeId)?.role ?? "guest") === "owner";
}

// Edit is offered when the person may change it (its can.edit), it doesn't
// run on the device itself, and it isn't a moved automation the old system
// still runs: the API answers 409 to its trigger, conditions or devices until
// it's switched over (app/screens/Automation/AutomationDetail.js).
export const canEditAutomation = (automation) =>
  Boolean(automation?.can?.edit) && !automation.readOnly && !(automation.legacy && automation.legacy.switched !== true);

// The home's automations, or one device's (which also lists what the device
// does by itself). items is null until the first answer.
export function useAutomations({ applianceId } = {}) {
  const { homeId, on } = useAutomationFeature();
  const [state, setState] = useState({ items: null, loading: true, error: null });
  const latest = useRef(0);

  const reload = useCallback(async () => {
    const call = ++latest.current;
    if (!homeId || !on) {
      setState({ items: [], loading: false, error: null });
      return;
    }
    setState((current) => ({ ...current, loading: true }));
    try {
      const items = await AutomationService.list(homeId, applianceId ? { applianceId } : {});
      if (call === latest.current) setState({ items: Array.isArray(items) ? items : [], loading: false, error: null });
    } catch (error) {
      if (call === latest.current) setState((current) => ({ items: current.items ?? [], loading: false, error: errorMessage(error) }));
    }
  }, [homeId, on, applianceId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Put a changed automation in place without reloading everything.
  const replace = useCallback((item) => {
    setState((current) => ({ ...current, items: (current.items ?? []).map((old) => (old.id === item.id ? item : old)) }));
  }, []);

  return { ...state, reload, replace };
}

// Home's Recent Automations: the latest run of each automation in the past 7
// days, newest first. runs is null until the first answer.
export function useRecentRuns({ limit = 10 } = {}) {
  const { homeId, on } = useAutomationFeature();
  const [state, setState] = useState({ runs: null, error: null });

  const reload = useCallback(async () => {
    if (!homeId || !on) {
      setState({ runs: [], error: null });
      return;
    }
    try {
      const { runs } = await AutomationService.history(homeId, { limit: 50 });
      const since = moment().subtract(7, "days");
      const seen = new Set();
      const latest = (runs || []).filter((run) => {
        if (seen.has(run.automationId) || moment(run.at).isBefore(since)) return false;
        seen.add(run.automationId);
        return true;
      });
      setState({ runs: latest.slice(0, limit), error: null });
    } catch (error) {
      setState((current) => ({ runs: current.runs ?? [], error: errorMessage(error) }));
    }
  }, [homeId, on, limit]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { ...state, reload };
}

// Pages of run history: one automation's, or the whole home's.
export function useRunHistory(automationId) {
  const { homeId, on } = useAutomationFeature();
  const [state, setState] = useState({ runs: [], next: null, loading: true, error: null });
  const busy = useRef(false);

  const load = useCallback(
    async (before) => {
      if (!homeId || !on) {
        setState({ runs: [], next: null, loading: false, error: null });
        return;
      }
      if (busy.current) return;
      busy.current = true;
      setState((current) => ({ ...current, loading: true }));
      try {
        const page = { limit: 30, before };
        const answer = automationId
          ? await AutomationService.runs(homeId, automationId, page)
          : await AutomationService.history(homeId, page);
        setState((current) => ({
          runs: before ? [...current.runs, ...(answer.runs || [])] : answer.runs || [],
          next: answer.next || null,
          loading: false,
          error: null,
        }));
      } catch (error) {
        setState((current) => ({ ...current, loading: false, error: errorMessage(error) }));
      } finally {
        busy.current = false;
      }
    },
    [homeId, on, automationId]
  );

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: () => load(), loadMore: () => state.next && load(state.next) };
}

// The app's confirmation pop-up as a promise: `await confirm({ title,
// message, confirmLabel })` is true for the confirm button. Render `dialog`
// once on the page.
export function useConfirm() {
  const [request, setRequest] = useState(null);
  const pending = useRef(null);

  const confirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        pending.current?.(false);
        pending.current = resolve;
        setRequest(options);
      }),
    []
  );

  const answer = (value) => {
    pending.current?.(value);
    pending.current = null;
    setRequest(null);
  };

  const dialog = (
    <AlertDialog
      open={Boolean(request)}
      type="confirmation"
      title={request?.title}
      message={request?.message}
      confirmLabel={request?.confirmLabel ?? "Yes"}
      onClose={() => answer(false)}
      onConfirm={() => answer(true)}
    />
  );

  return { confirm, dialog };
}

// The API's "confirm first" answer: its message and the locks, gates, alarms
// or breakers it names, one per line (the app's confirmText).
export const confirmMessage = (error) => (
  <>
    {error.message}
    {(error.confirm || []).map((device) => (
      <React.Fragment key={device.applianceId ?? device.name}>
        <br />• {device.name}
      </React.Fragment>
    ))}
  </>
);

// ▶ Run: runs it now and says how it went. Locks, gates, alarms and breakers
// are confirmed first.
export function useRunAutomation(confirm, onDone) {
  const { homeId } = useAutomationFeature();
  const toast = useToggleTop();

  return useCallback(
    async (automation) => {
      const report = (result) => {
        if (result?.status === "failed") toast(result.message || `${automation.name} didn't run.`, "red");
        else if (result?.status === "skipped") toast(result.message || `${automation.name} was skipped.`, "blue");
        else toast(`${automation.name} ran.`, "green");
        onDone?.(result);
      };
      try {
        report(await AutomationService.run(homeId, automation.id));
      } catch (error) {
        if (error?.status === 409 && error.confirm) {
          if (await confirm({ title: "Run it?", message: confirmMessage(error), confirmLabel: "Run" })) {
            try {
              report(await AutomationService.run(homeId, automation.id, true));
            } catch (retryError) {
              toast(errorMessage(retryError), "red");
            }
          }
          return;
        }
        toast(errorMessage(error), "red");
      }
    },
    [homeId, toast, confirm, onDone]
  );
}

// The on/off switch: shows the new state straight away and puts it back if
// saving fails.
export function useToggleAutomation(replace) {
  const { homeId } = useAutomationFeature();
  const toast = useToggleTop();

  return useCallback(
    async (automation, enabled) => {
      replace({ ...automation, enabled });
      try {
        replace(await AutomationService.setEnabled(homeId, automation.id, enabled));
      } catch (error) {
        replace(automation);
        toast(errorMessage(error), "red");
      }
    },
    [homeId, replace, toast]
  );
}
